import {
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Button,
  MenuItem,
  Grid,
  FormControlLabel,
  Switch,
  Typography,
} from "@material-ui/core";
import React, { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { validationFromSchema } from "../util";
import { $Environment, Environment, Project } from "../_generated";
import { Service } from "../_generated/models/Service";
import { RdsEnvironmentConfig, rdsInstanceTypes } from "../rds";
import { ProjectResource } from "../models/ProjectResource";

const ContainerTypeSelection = (props: {
  services: Service[];
  control: any;
}) => {
  if (props.services.some((service) => service.service_type === "container")) {
    return (
      <Grid item xs={12}>
        <Controller
          control={props.control}
          name="container_target"
          defaultValue="fargate"
          render={({ onChange, value, ref }) => (
            <TextField
              select
              inputRef={ref}
              onChange={onChange}
              value={value}
              variant="outlined"
              label="Container Target"
              fullWidth
            >
              <MenuItem key="fargate" value="fargate">
                fargate
              </MenuItem>
              <MenuItem key="eks" value="eks">
                eks
              </MenuItem>
            </TextField>
          )}
        />
      </Grid>
    );
  } else {
    return <div></div>;
  }
};

export default function EnvironmentCreateDialog(props: {
  project: Project;
  open: boolean;
  isFirst?: boolean;
  environment?: Environment;
  services: Service[];
  resources: ProjectResource[];
  onCancel: () => any;
  onSubmit: (data: Environment, setErrors: any) => any;
}) {
  const { register, handleSubmit, errors, control } = useForm({
    shouldUnregister: true,
  });
  const [formErrors, setFormErrors] = useState({} as { general: string });

  return (
    <Dialog open={props.open}>
      <form
        onSubmit={handleSubmit((data: any) => {
          const targetData = buildTargetData(data);
          const environmentData = buildEnvironmentData(data, props.resources);
          props.onSubmit(
            {
              ...props.environment,
              ...environmentData,
              ...targetData,
            } as Environment,
            setFormErrors
          );
        })}
      >
        <DialogTitle>New Environment</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12} spacing={2}>
              <Typography color="error">{formErrors["general"]}</Typography>
            </Grid>

            <Grid item xs={12}>
              <TextField
                inputRef={register(
                  validationFromSchema($Environment.properties.name)
                )}
                variant="outlined"
                label="Name"
                name="alias"
                required
                defaultValue={
                  props.environment?.name ?? (props.isFirst ? "production" : "")
                }
                error={errors.name !== undefined}
                helperText={errors.name?.message}
                fullWidth
              />
            </Grid>
            {/*TODO update / move Environment model from src/_generated. It does not know about ci_branch. */}
            <Grid item xs={12}>
              <TextField
                inputRef={register}
                variant="outlined"
                label="CI branch"
                name="ci_branch"
                defaultValue={props.isFirst ? "master" : ""}
                error={errors.target !== undefined}
                helperText={errors.target?.message}
                fullWidth
              />
            </Grid>
            <ContainerTypeSelection
              services={props.services}
              control={control}
            />

            <Grid item xs={12}>
              <Controller
                control={control}
                name="region"
                defaultValue={
                  props.project?.default_aws_region ?? awsRegions[0]
                }
                render={({ onChange, value, ref }) => (
                  <TextField
                    select
                    label="Region"
                    variant="outlined"
                    value={value}
                    onChange={onChange}
                    inputRef={ref}
                    fullWidth
                    disabled={props.project.is_paas ? true : false}
                  >
                    {awsRegions.map((region) => (
                      <MenuItem value={region} key={region}>
                        {region}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            </Grid>

            {props.resources
              .filter((r) => r.resource_type === "RDS_POSTGRES")
              .map((resource) => (
                <>
                  <FormControlLabel
                    control={
                      <Switch
                        name={resource.pk + "__enabled"}
                        inputRef={register}
                        defaultChecked
                      />
                    }
                    label={resource.name + " enabled"}
                  />

                  <Grid item xs={12}>
                    <Controller
                      control={control}
                      name={resource.pk + "__rds_instance_type"}
                      defaultValue="db.t3.micro"
                      render={({ onChange, value, ref }) => (
                        <TextField
                          select
                          inputRef={ref}
                          label={resource.name + " RDS Instance Size"}
                          variant="outlined"
                          value={value}
                          onChange={onChange}
                          fullWidth
                        >
                          {rdsInstanceTypes.map((instanceType) => (
                            <MenuItem value={instanceType} key={instanceType}>
                              {instanceType}
                            </MenuItem>
                          ))}
                        </TextField>
                      )}
                    />
                  </Grid>
                </>
              ))}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setFormErrors({ general: "" });
              props.onCancel();
            }}
          >
            Cancel
          </Button>
          <Button type="submit" variant="contained" color="primary">
            Create and Deploy
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}

const awsRegions = [
  "us-east-1",
  "us-east-2",
  "us-west-1",
  "us-west-2",
  "af-south-1",
  "ap-east-1",
  "ap-south-1",
  "ap-northeast-3",
  "ap-northeast-2",
  "ap-northeast-1",
  "ap-southeast-1",
  "ap-southeast-2",
  "ca-central-1",
  "cn-north-1",
  "cn-northwest-1",
  "eu-west-1",
  "eu-west-2",
  "eu-west-3",
  "eu-north-1",
  "eu-south-1",
  "me-south-1",
  "sa-east-1",
  "us-gov-east-1",
  "us-gov-west-1",
];

function buildEnvironmentData(formData: any, resources: ProjectResource[]) {
  const configOptions = resources.map((resource) => {
    const options: RdsEnvironmentConfig = {
      resource_id: resource.pk,
    };
    if (resource.resource_type === "RDS_POSTGRES") {
      options.enabled = formData[resource.pk + "__enabled"];
      options.rds_instance_type = formData[resource.pk + "__rds_instance_type"];
    }
    return options;
  });
  const dataWithoutConfigOptions = Object.fromEntries(
    Object.entries(formData).filter(([key]) => !key.includes("__"))
  );
  return {
    ...dataWithoutConfigOptions,
    resource_config_options: configOptions,
    //TODO ^ dirty typing suppression - config_options not included in any typings
  };
}

function buildTargetData(formData: any) {
  let target_data = {};
  if (formData.container_target === "eks") {
    target_data = {
      target: "diggerhq/target-aws@v2.0.7",
      container_service_target: "eks",
    };
  }
  return target_data;
}
